import { useState, useEffect } from 'react';
import { X, Building2, Search, CheckCircle, XCircle } from 'lucide-react';
import { useFeatureFlags } from '../../../../hooks/platform-admin/useFeatureFlags';
import { usePlatformOrganizations } from '../../../../hooks/platform-admin/usePlatformOrganizations';
import { FeatureFlag, OrganizationFeatureFlag } from '../../../../services/platform-admin/feature-flags.service';
import { Organization } from '../../../../services/platform-admin/platform-organizations.service';

interface ManageOrganizationsModalProps {
  flag: FeatureFlag;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ManageOrganizationsModal({
  flag,
  onClose,
  onSuccess,
}: ManageOrganizationsModalProps) {
  const { getOrganizationsWithFlag, enableForOrganizations, disableForOrganizations } = useFeatureFlags();
  const { getOrganizations } = usePlatformOrganizations();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [flagOrgs, setFlagOrgs] = useState<OrganizationFeatureFlag[]>([]);
  const [selectedOrgs, setSelectedOrgs] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    const [allOrgs, withFlag] = await Promise.all([
      getOrganizations(),
      getOrganizationsWithFlag(flag.id),
    ]);

    if (allOrgs?.data) {
      setOrganizations(allOrgs.data);
    }
    if (withFlag) {
      setFlagOrgs(withFlag);
    }
    setIsLoading(false);
  };

  const isEnabled = (orgId: string): boolean => {
    const flagOrg = flagOrgs.find((fo) => fo.organizationId === orgId);
    return flagOrg ? flagOrg.isEnabled : false;
  };

  const toggleOrganization = (orgId: string) => {
    const newSelected = new Set(selectedOrgs);
    if (newSelected.has(orgId)) {
      newSelected.delete(orgId);
    } else {
      newSelected.add(orgId);
    }
    setSelectedOrgs(newSelected);
  };

  const handleEnableSelected = async () => {
    if (selectedOrgs.size === 0) {
      setError('Please select at least one organization');
      return;
    }

    setError('');
    setIsLoading(true);

    const success = await enableForOrganizations(flag.id, {
      organizationIds: Array.from(selectedOrgs),
    });

    if (success) {
      setSelectedOrgs(new Set());
      await loadData();
    }

    setIsLoading(false);
  };

  const handleDisableSelected = async () => {
    if (selectedOrgs.size === 0) {
      setError('Please select at least one organization');
      return;
    }

    setError('');
    setIsLoading(true);

    const success = await disableForOrganizations(flag.id, {
      organizationIds: Array.from(selectedOrgs),
    });

    if (success) {
      setSelectedOrgs(new Set());
      await loadData();
    }

    setIsLoading(false);
  };

  const filteredOrganizations = organizations.filter((org) =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const enabledCount = flagOrgs.filter((fo) => fo.isEnabled).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Manage Organizations</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {flag.name} - {enabledCount} organization{enabledCount !== 1 ? 's' : ''} enabled
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Search and Actions */}
        <div className="p-6 border-b border-gray-200 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search organizations..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleEnableSelected}
              disabled={isLoading || selectedOrgs.size === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              Enable ({selectedOrgs.size})
            </button>
            <button
              onClick={handleDisableSelected}
              disabled={isLoading || selectedOrgs.size === 0}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              Disable ({selectedOrgs.size})
            </button>
          </div>

          <p className="text-xs text-gray-500">
            Select organizations and use the buttons above to enable or disable this feature flag.
          </p>
        </div>

        {/* Organizations List */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && organizations.length === 0 ? (
            <div className="text-center text-gray-500 py-12">Loading organizations...</div>
          ) : filteredOrganizations.length === 0 ? (
            <div className="text-center text-gray-500 py-12">No organizations found</div>
          ) : (
            <div className="space-y-2">
              {filteredOrganizations.map((org) => {
                const enabled = isEnabled(org.id);
                const selected = selectedOrgs.has(org.id);

                return (
                  <div
                    key={org.id}
                    onClick={() => toggleOrganization(org.id)}
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                      selected
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => {}}
                      className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-900">{org.name}</div>
                        <span
                          className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                            org.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : org.status === 'PAUSED'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {org.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{org.id}</div>
                    </div>
                    {enabled ? (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-xs font-medium">Enabled</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-gray-400">
                        <XCircle className="h-4 w-4" />
                        <span className="text-xs font-medium">Disabled</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onSuccess}
            className="px-4 py-2 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
